const SUBMISSION_SUCCESS = "SUCCESS",
    SUBMISSION_FAIL = "FAILURE",
    SUBMISSION_ABORTED = "ABORTED";

// Given mutation callbacks, return a function that can upload an array of photos
export const setupUploadPhotos = (
    registerPhotoUploads,
    reportAwsPhotoUploadResult,
    photoShootId,
    handleRegisterUploadsResult
) => {
    return uploadPhotos;

    async function uploadPhotos(files) {
        let uploadErrorsByFilename = {};

        if (!files || !files.length) {
            return { errors: uploadErrorsByFilename };
        }

        const {
            preUploads,
            errorsByFilename: uploadRegistrationErrorsByFilename
        } = await getPhotoPreUploads({
            registerPhotoUploads,
            filenames: getFilenames(files),
            photoShootId,
            handleRegisterUploadsResult,
            files
        });

        await Promise.all(preUploads.map(uploadAndReport));

        return {
            errors: {
                ...uploadErrorsByFilename,
                ...uploadRegistrationErrorsByFilename
            }
        };

        function getFilenames(files) {
            files = [...files]; // make a real array
            return files.map(file => file.name);
        }

        async function uploadAndReport({ id, filename, url }, i) {
            const uploadSuccess = await uploadToAWS({
                url,
                file: files.find(file => file.name === filename),
                contentType: "image/jpeg"
            });

            const errorMessage = await reportAwsUploadResult({
                id,
                uploadSuccess,
                mutateFunction: reportAwsPhotoUploadResult,
                mutationName: "photoUploadFinishedNotification"
            });

            if (errorMessage) {
                uploadErrorsByFilename[filename] = errorMessage;
            }
        }
    }
};

// Given mutation callbacks, return a function that can upload an array of releases
export const setupUploadReleases = (uploadRelease, submitRelease) => {
    return async files => {
        let errors = {}; // { [filename]: error_message }
        let uploadedReleases = [];

        if (!files || !files.length) {
            return { errors };
        }

        files = [...files]; // make a real array

        // NOTE: Break up uploads by content-type.
        const filesByType = files.reduce((filesByType, file) => {
            const type = file.type;
            if (!filesByType[type]) {
                filesByType[type] = [file];
            } else {
                filesByType[type] = [...filesByType[type], file];
            }
            return filesByType;
        }, {});
        const contentTypes = Object.keys(filesByType);

        await Promise.all(
            contentTypes.map(async contentType => {
                const files = filesByType[contentType];
                const filenames = files.map(file => file.name);
                const { uploads, errors: urlErrors } = await getUploadUrls({
                    uploadFile: uploadRelease,
                    filenames,
                    mutationName: "photoReleaseUpload"
                });

                if (urlErrors) {
                    Object.assign(errors, urlErrors);
                }

                if (uploads.length === 0) return;

                await Promise.all(
                    uploads.map(async (file, i) => {
                        const { id, filename, url } = file;
                        const uploadSuccess = await uploadToAWS({
                            url,
                            file: files[i],
                            contentType
                        });

                        const errorMessage = await reportAwsUploadResult({
                            id,
                            uploadSuccess,
                            mutateFunction: submitRelease,
                            mutationName:
                                "photoReleaseUploadFinishedNotification"
                        });
                        if (errorMessage) {
                            errors[filename] = errorMessage;
                        } else {
                            // the UI looks for a name field
                            file.name = filename;
                            uploadedReleases.push(file);
                        }
                    })
                );
            })
        );

        return { errors, uploadedReleases };
    };
};

async function getPhotoPreUploads({
    registerPhotoUploads,
    filenames,
    files,
    photoShootId,
    handleRegisterUploadsResult
}) {
    const { data } = await registerPhotoUploads({
        variables: {
            filenames,
            photoShootId
        }
    });

    const errorsByFilename = {};

    const errors = data.photoUpload.errors.map(({ filename, reason }) => {
        errorsByFilename[filename] = reason;

        return {
            filename,
            error: reason,
            file: files.find(file => file.name === filename)
        };
    });

    handleRegisterUploadsResult(errors);

    return { preUploads: data.photoUpload.uploads, errorsByFilename };
}

// Given files, return upload URLs (or errors, if they occur)
async function getUploadUrls({ uploadFile, filenames, mutationName }) {
    let errors = {};

    const res = await uploadFile({
        variables: {
            filenames
        }
    });

    if (
        res.data &&
        res.data[mutationName] &&
        res.data[mutationName].errors &&
        res.data[mutationName].errors.length
    ) {
        res.data[mutationName].errors.forEach(({ filename, reason }) => {
            errors[filename] = reason;
        });
    }

    const uploads =
        res.data &&
        res.data[mutationName] &&
        Array.isArray(res.data[mutationName].uploads) &&
        res.data[mutationName].uploads.length
            ? res.data[mutationName].uploads
            : [];

    return { uploads, errors };
}

// Given file, upload to S3, then return bool of successfulness
async function uploadToAWS({ url, file, contentType }) {
    const body = new Blob([await file.arrayBuffer()], {
        type: contentType
    });
    const uploadSuccess = await fetch(url, {
        method: "PUT",
        headers: {
            "Content-Type": contentType // required by the S3 upload URL
        },
        body
    })
        .then(s3Response => s3Response.status === 200) // if s3 rejects file
        .catch(() => false); // if network errors occur

    return uploadSuccess;
}

// Given file, confirm with backend that S3 upload was successful
function reportAwsUploadResult({
    id,
    uploadSuccess,
    mutateFunction,
    mutationName
}) {
    const FAIL_MESSAGE = "Failed to upload";

    return mutateFunction({
        variables: {
            id,
            result: uploadSuccess ? SUBMISSION_SUCCESS : SUBMISSION_FAIL
        }
    })
        .then(({ data }) => {
            if (!uploadSuccess) {
                return FAIL_MESSAGE;
            } else if (data && data[mutationName] && data[mutationName].error) {
                return data[mutationName].error;
            }
        })
        .catch(() => {
            return FAIL_MESSAGE;
        });
}

export const setupCancelRelease = submitRelease => {
    const mutationName = "photoReleaseUploadFinishedNotification";
    return async ({ id }) => {
        const error = await submitRelease({
            variables: {
                id,
                result: SUBMISSION_ABORTED
            },
            // TODO: probably should update cache instead
            refetchQueries: ["listPhotoUploads"]
        })
            .then(({ data }) => {
                if (data && data[mutationName] && data[mutationName].error) {
                    return data[mutationName].error;
                }
                return null;
            })
            .catch(() => {
                return "Failed to remove photo release"; // network issues, etc.
            });

        return { error };
    };
};

// Given results of pending-photos query, return formatted photos
export const formatPhotos = data => {
    data = data.filter(upload => upload.status === "IN_DROPZONE");

    return Promise.all(
        data.map(async record => {
            const res = await fetch(record.thumbnail); //todo: pick bigger thumbnail?
            const data = await res.blob();
            const file = new File([data], record.originalFilename, {
                type: "image/jpeg"
            });

            //only add record if thumbnail exists so as to not push into array of objects prematurely:
            if (record.thumbnail) {
                return {
                    ...record,
                    ratio: record.aspectRatio,
                    id: record.id,
                    preview: URL.createObjectURL(file), // creates file preview
                    lastModified: file.lastModified,
                    lastModifiedDate: file.lastModifiedDate,
                    name:
                        record.photoTitle ||
                        record.originalFilename ||
                        file.name,
                    size: file.size,
                    type: file.type,
                    userTags: record.userTags,
                    warnings: {},
                    errors: {
                        message: record.errors
                    }
                };
            }
        })
    ).then(files => {
        return files.sort((a, b) => {
            return a.id - b.id; // sort in ascending order based on ID
        });
    });
};
